import dayjs from "dayjs";
// Self-contained UTC extension — providers/LocalizationProvider.tsx already
// extends utc at boot, but registering here too keeps the file safe in
// isolation (e.g. unit tests, future stories that bypass app providers).
// `dayjs.extend` is a no-op when the plugin is already registered.
import utc from "dayjs/plugin/utc";
import * as React from "react";

import { type DateComparator, isValueless } from "../FilterSchema";

import styles from "./DateValueInput.module.css";

dayjs.extend(utc);

interface DateValueInputProps {
  comparator: DateComparator;
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * UTC-anchored date helpers — the date input uses UTC midnight throughout
 * to keep the round trip ISO ↔ "YYYY-MM-DD" stable across timezones. Local
 * timezone interpretation lands as a follow-up if users complain about
 * off-by-one-day filtering near midnight.
 */
function fromIsoToInputValue(iso: string | undefined): string {
  // ISO 8601 → YYYY-MM-DD. Slice avoids a Date round trip and the
  // timezone shift that would come with it.
  return iso ? iso.slice(0, 10) : "";
}

function inputValueToIso(yyyyMmDd: string): string {
  // YYYY-MM-DD with no timezone → UTC midnight ISO. Empty input → empty
  // string so callers can omit the value cleanly.
  if (yyyyMmDd === "") {
    return "";
  }
  return dayjs.utc(yyyyMmDd, "YYYY-MM-DD").startOf("day").toISOString();
}

/**
 * Value input for date-typed filters.
 *
 * Operator-aware:
 *   - BETWEEN renders two date pickers (start + end); both must be filled.
 *   - LESS_THAN ("before") / GREATER_THAN ("after") render a single date picker.
 *   - Relative presets (TODAY / LAST_N_DAYS / THIS_MONTH) are valueless —
 *     `isValueless` short-circuits this component to render nothing, and
 *     `filterToCondition` resolves the range from `now` at query time.
 *
 * Values are ISO 8601 strings anchored to UTC midnight (or end-of-day for
 * the upper bound). BETWEEN is a UI-only comparator that `filterToCondition`
 * expands into an AND of GREATER_THAN_OR_EQUAL / LESS_THAN_OR_EQUAL; the
 * single-value comparators pass through to the wire as-is.
 */
export function DateValueInput({
  comparator,
  value,
  onChange,
}: DateValueInputProps) {
  const valueless = isValueless(comparator);
  const isBetween = comparator === "BETWEEN";

  // Normalize the date arity.
  // If the user switches between BETWEEN -> BEFORE/AFTER, this truncates the value
  // so stale values aren't kept. Runs before paint.
  React.useLayoutEffect(() => {
    if (!valueless && !isBetween && value.length > 1) {
      onChange(value.slice(0, 1));
    }
  }, [valueless, isBetween, value, onChange]);

  if (valueless) {
    return null;
  }

  const handleSingleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const iso = inputValueToIso(event.target.value);
    onChange(iso === "" ? [] : [iso]);
  };

  const handleRangeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: 0 | 1,
  ) => {
    const iso = inputValueToIso(event.target.value);
    const next: string[] = [value[0] ?? "", value[1] ?? ""];
    next[index] = iso;
    onChange(next);
  };

  return (
    <div className={styles.input}>
      {isBetween ? (
        <div className={styles.range}>
          <input
            type="date"
            className={styles.field}
            aria-label="Start date"
            value={fromIsoToInputValue(value[0])}
            onChange={(event) => handleRangeChange(event, 0)}
          />
          <span className={styles.rangeSep} aria-hidden="true">
            →
          </span>
          <input
            type="date"
            className={styles.field}
            aria-label="End date"
            value={fromIsoToInputValue(value[1])}
            onChange={(event) => handleRangeChange(event, 1)}
          />
        </div>
      ) : (
        <input
          type="date"
          className={styles.field}
          aria-label="Date"
          value={fromIsoToInputValue(value[0])}
          onChange={handleSingleChange}
        />
      )}
    </div>
  );
}
